import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronUp } from 'lucide-react';

/** Scrolls window to top whenever the route pathname changes. */
export function ScrollRestorer() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
  }, [pathname]);
  return null;
}

/** Floating "back to top" chevron — fades in once the user has scrolled past the threshold. */
export function ScrollToTopButton({ threshold = 320 }: { threshold?: number }) {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const onScroll = () => setVisible(window.scrollY > threshold);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, [threshold]);

  return (
    <AnimatePresence>
      {visible ? (
        <motion.button
          type="button"
          aria-label="Back to top"
          onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 0.7, y: 0 }}
          exit={{ opacity: 0, y: 16 }}
          whileHover={{ opacity: 1, y: -2, scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          transition={{ duration: 0.2 }}
          className="fixed bottom-6 right-6 z-40 w-12 h-12 rounded-full bg-dark-surface/70 backdrop-blur-md border border-dark-border-light shadow-lg shadow-dark-bg/40 text-content flex items-center justify-center hover:border-indigo-500/60 hover:bg-dark-surface/90 transition-colors"
        >
          <ChevronUp size={22} />
        </motion.button>
      ) : null}
    </AnimatePresence>
  );
}
